import { z } from 'zod';

// Thread participant schema
export const threadParticipantSchema = z.object({
  type: z.enum(['user', 'entity', 'item']),
  id: z.number().int().positive(),
  role: z.string().max(50).optional(),
});

// Create thread schema
// Note: participants.min(1) because the creator is automatically added to participants if not already included
export const createThreadSchema = z.object({
  name: z.string().max(200).optional(),
  participants: z.array(threadParticipantSchema).min(1),
  resourceType: z.enum(['entity', 'item']).optional(),
  resourceId: z.number().int().positive().optional(),
  threadType: z.string().max(100).optional(),
  initialMessage: z.object({
    messageType: z.string().max(100).optional(),
    subject: z.string().max(500).optional(),
    message: z.string().min(1).max(5000),
    data: z.record(z.string(), z.any()).optional(),
  }),
});

// Update thread schema
// Note: participants.min(1) to allow single-participant threads (though typically 2+)
export const updateThreadSchema = z.object({
  name: z.string().max(200).optional(),
  participants: z.array(threadParticipantSchema).min(1).optional(),
  archived: z.boolean().optional(),
});

// Send message schema
export const sendMessageSchema = z.object({
  message: z.string().min(1).max(5000),
  messageType: z.string().max(100).optional(),
  data: z.record(z.string(), z.any()).optional(),
});

// Sample request data schema
export const sampleRequestDataSchema = z.object({
  items: z
    .array(
      z.object({
        resourceType: z.string(),
        resourceId: z.number().int().positive(),
        quantity: z.number().int().positive(),
      })
    )
    .min(1),
  deliveryAddress: z.object({
    street: z.string().min(1),
    city: z.string().min(1),
    state: z.string().min(1),
    postalCode: z.string().min(1),
    country: z.string().min(1),
  }),
  status: z.enum(['pending', 'approved', 'rejected', 'fulfilled']).optional(),
  notes: z.string().max(1000).optional(),
});

// Meeting request data schema
export const meetingRequestDataSchema = z.object({
  meetingType: z.enum(['phone', 'email', 'video', 'in_person']),
  phoneNumber: z.string().optional(),
  videoPlatform: z.string().optional(),
  location: z.string().optional(),
  proposedTimes: z
    .array(
      z.object({
        start: z.string().datetime(),
        end: z.string().datetime(),
      })
    )
    .min(1),
  status: z.enum(['pending', 'accepted', 'declined']).optional(),
  notes: z.string().max(1000).optional(),
});

// Trade hub settings schema
export const tradeHubSettingsSchema = z.object({
  visibility: z.enum(['public', 'user_trade_partners', 'approved_partners', 'specific_users_only']),
  allowedUserIds: z.array(z.number().int().positive()).optional(),
  details: z.string().max(10000).optional(),
  contactDetails: z
    .object({
      email: z.string().email().optional(),
      phone: z.string().optional(),
      website: z.string().url().optional(),
    })
    .optional(),
  minOrderQuantity: z.number().int().positive().optional(),
  terms: z.string().max(5000).optional(),
  images: z.array(z.string().url()).optional(),
  documents: z
    .array(
      z.object({
        name: z.string(),
        url: z.string().url(),
        size: z.number().int().positive(),
      })
    )
    .optional(),
  sampleRequestsEnabled: z.boolean().optional(),
  meetingRequestsEnabled: z.boolean().optional(),
});
